import type { ReactNode } from 'react';
import { useLensState } from './lensTags';

/**
 * ONE TAG, ONE LOOK. The same tag was drawn five ways: filled purple in the
 * tools table, a grey outline on a product page and in the chat's tables, and
 * green or grey outlines on item cards and in the drawer. Every place that
 * shows a tag uses this.
 *
 * - The value is the label; the dimension is on hover, or a lighter prefix
 *   where it matters (`showDimension`): an item's own page, the drawer, a card.
 * - With `onPick` it is a button that puts the tag in the lens or takes it
 *   out, and `picked` fills it in while it is in the lens.
 * - IN THE LENS, IT LOOKS IT, everywhere: dark blue when the followed lead
 *   holds it, light blue when it was added to the lens past the lead. The page
 *   says which through `LensTagsContext`, so no caller has to. A lead's tag is
 *   not a button: Back on the lead bar is how you leave a lead.
 * - `approved` adds a small check: a person or the skeptic confirmed it.
 * - `children` sit inside it, after the value: the drawer's approve and
 *   remove buttons.
 */
export function tagWords(dimension: string): string {
  return dimension.toLowerCase().replace(/_/g, ' ');
}

export default function TagChip({
  dimension,
  value,
  showDimension = false,
  picked = false,
  onPick,
  approved,
  title,
  testId,
  children,
}: {
  dimension: string;
  value: string;
  showDimension?: boolean;
  picked?: boolean;
  onPick?: () => void;
  approved?: boolean;
  title?: string;
  testId?: string;
  children?: ReactNode;
}) {
  const where = tagWords(dimension);
  const state = useLensState(dimension, value) ?? (picked ? 'lens' : null);
  const held = state === 'lead';
  const pick = held ? undefined : onPick;
  const hover =
    title ??
    (held
      ? `Part of the lead you are following: ${where}: ${value}. Use Back on the lead bar to leave it.`
      : pick
        ? state
          ? `In the lens: ${where}: ${value}. Click to take it out.`
          : `${where}: ${value}. Click to add it to the lens.`
        : `${where}: ${value}${approved ? ' · approved' : ''}`);
  const look = `inline-flex max-w-full items-center gap-1 rounded px-1.5 py-px text-[11px] leading-4 ${
    held
      ? 'bg-indigo-500 text-white dark:bg-indigo-400'
      : state
        ? 'bg-sky-100 text-sky-900 ring-1 ring-sky-300 dark:bg-sky-900/50 dark:text-sky-100 dark:ring-sky-700'
        : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
  }`;
  const body = (
    <>
      {approved && (
        <span aria-label="approved" className={held ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'}>
          ✓
        </span>
      )}
      <span className="min-w-0 break-words">
        {showDimension && <span className="opacity-70">{where}: </span>}
        {value}
      </span>
      {children}
    </>
  );
  return pick ? (
    <button
      type="button"
      onClick={pick}
      aria-pressed={state === 'lens'}
      data-lens={state ?? undefined}
      title={hover}
      className={`${look} text-left ${state ? 'hover:bg-sky-200 dark:hover:bg-sky-900' : 'hover:bg-violet-100 dark:hover:bg-violet-900/60'}`}
      data-testid={testId}
    >
      {body}
    </button>
  ) : (
    <span title={hover} className={look} data-testid={testId} data-lens={state ?? undefined}>
      {body}
    </span>
  );
}
